import { classifyAddress, type AddressKind, type IceCandidateRecord } from "@suite/lib/webrtc";
import { cn } from "@suite/lib/utils";

/* One line of the ICE candidate log, shared by the scanning chassis (rows appear live while
 * the probe runs) and the finished report card, so a candidate the visitor watched arrive is
 * the exact row the report keeps. Presentational and hook-free: both a Server Component
 * (loading.tsx via WebRtcScanning) and the client instrument can render it. */

/** What each candidate type means, in words a non-expert can use. Also the sr-only text. */
export const CANDIDATE_TYPE_HINTS: Record<string, string> = {
  host: "A local interface address as this device sees it. Modern browsers usually replace it with a random .local name.",
  srflx:
    "Server-reflexive: the public address a STUN server saw for this browser's request. This is the classic WebRTC leak path.",
  prflx: "Peer-reflexive: an address the connection discovered mid-handshake.",
  relay: "A TURN relay address. It belongs to the relay server, not to this device.",
  unknown: "A candidate type this tool doesn't recognise.",
};

/** Address colour carries its classification; the kind word next to it says the same thing in
 * text so the state is never colour-only. */
const KIND_TEXT: Record<AddressKind, { word: string; className: string }> = {
  public: { word: "public", className: "text-amber-300" },
  private: { word: "private", className: "text-white/70" },
  mdns: { word: "masked", className: "text-brand-green" },
  loopback: { word: "loopback", className: "text-white/50" },
  linkLocal: { word: "link-local", className: "text-white/50" },
  cgnat: { word: "cgnat", className: "text-white/70" },
  reserved: { word: "reserved", className: "text-white/50" },
  unparseable: { word: "unreadable", className: "text-amber-300" },
};

interface Props {
  candidate: IceCandidateRecord;
}

export default function IceCandidateRow({ candidate }: Props) {
  const kind = classifyAddress(candidate.address);
  const meta = KIND_TEXT[kind];
  return (
    <li className="-mx-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 rounded border-b border-white/[0.04] px-1.5 py-1.5 font-mono text-[11px] last:border-0 hover:bg-white/[0.025]">
      <span className="w-14 shrink-0 text-white/45 tabular-nums">
        +{(candidate.atMs / 1000).toFixed(2)}s
      </span>
      <span className="w-10 shrink-0 font-semibold tracking-widest text-white/70 uppercase">
        {candidate.type}
        <span className="sr-only">. {CANDIDATE_TYPE_HINTS[candidate.type]}</span>
      </span>
      <span className="w-8 shrink-0 text-white/50 uppercase">{candidate.protocol}</span>
      <span className={cn("min-w-0 [overflow-wrap:anywhere] break-words", meta.className)}>
        {candidate.address}
        {candidate.port !== null && <span className="text-white/45">:{candidate.port}</span>}
      </span>
      <span className={cn("ml-auto shrink-0 text-[9px] tracking-widest uppercase", meta.className)}>
        {meta.word}
      </span>
    </li>
  );
}
